import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useShortlist } from "@/context/ShortlistContext";
import { mediaUrl } from "@/lib/api";
import { voicePath } from "@/config/childVoices";
import SEO from "@/components/SEO";
import BookingForm from "@/components/BookingForm";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Heart, ArrowRight } from "lucide-react";

export default function Shortlist() {
  const { items, remove } = useShortlist();
  const [deselected, setDeselected] = useState(() => new Set());
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);
  const selectRef = useRef(null);

  const isSelected = (id) => !deselected.has(id);
  const toggle = (id) => setDeselected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const selected = items.filter((t) => isSelected(t.id));

  const startRequest = () => {
    if (!selected.length) return;
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  return (
    <>
      <SEO title="Your Favorites" path="/shortlist" description="Review your favorite child voices and start a booking request." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow text-brand-purple">Favorites</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">Your favorites ({items.length})</h1>
        </div>
      </section>

      {items.length === 0 ? (
        <section className="container-x py-24 text-center">
          <Heart size={40} className="mx-auto mb-4 text-zinc-300" />
          <p className="font-semibold text-zinc-700">Your favorites list is empty.</p>
          <p className="text-zinc-500">Browse voices and tap the heart to add them here.</p>
          <Link to="/child-voices" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">Browse voices <ArrowRight size={16} /></Link>
        </section>
      ) : (
        <section className="container-x section-y overflow-x-clip">
          <div ref={selectRef} className="mx-auto max-w-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Select voice talents</h2>
              <span className="text-sm font-semibold text-zinc-500" data-testid="selected-count">{selected.length} selected</span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((t) => (
                <div key={t.id} data-testid={`favorite-${t.id}`} onClick={() => toggle(t.id)}
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border bg-white p-3 transition ${isSelected(t.id) ? "border-brand-violet ring-1 ring-brand-violet" : "border-zinc-200 hover:border-zinc-300"}`}>
                  <Checkbox checked={isSelected(t.id)} onCheckedChange={() => toggle(t.id)} onClick={(e) => e.stopPropagation()} data-testid={`favorite-check-${t.id}`} />
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
                    {t.photo_url && <img src={mediaUrl(t.photo_url)} alt={t.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={voicePath(t)} onClick={(e) => e.stopPropagation()} className="block truncate font-heading font-bold hover:text-brand-violet">{t.name}</Link>
                    <p className="truncate text-sm text-zinc-500">{t.language}{t.age_min ? ` · Age ${t.age_min === t.age_max ? t.age_min : `${t.age_min}–${t.age_max}`}` : ""}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); remove(t.id); }} aria-label={`Remove ${t.name} from favorites`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-red-500"><X size={18} /></button>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col items-start gap-2">
              <button onClick={startRequest} disabled={!selected.length} data-testid="start-booking"
                className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-7 py-4 font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover disabled:opacity-50">
                {selected.length === 1 ? "Start booking request" : `Request ${selected.length} voice talents`} <ArrowRight size={16} />
              </button>
              {showForm && <button onClick={() => selectRef.current?.scrollIntoView({ behavior: "smooth" })} className="text-sm font-semibold text-zinc-400 hover:text-brand-violet">↑ Change selection</button>}
            </div>
          </div>

          {showForm && selected.length > 0 && (
            <div ref={formRef} className="mx-auto mt-12 max-w-3xl rounded-3xl border border-zinc-200 bg-white p-6 md:p-8">
              <h2 className="font-heading text-2xl font-bold">Booking request</h2>
              <p className="mb-6 mt-1 text-zinc-600">Tell us about your project — this is a request, not a confirmed booking.</p>
              <BookingForm talents={items} initialSelectedIds={selected.map((t) => t.id)} />
            </div>
          )}
        </section>
      )}
    </>
  );
}
